
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Button from '../../components/base/Button';

export default function Services() {
  const { t } = useTranslation();

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Painting Services Atlanta - Interior, Exterior & Commercial | WF & Y Painting';
    
    // Add meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Professional painting services in Atlanta: interior painting, exterior painting, commercial painting, drywall repair, pressure washing, deck staining. Free estimates. Call 770-875-6990.');

    // Add structured data for services page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/services#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/services`,
      "name": "Painting Services Atlanta - Interior, Exterior & Commercial",
      "description": "Professional painting services in Atlanta: interior painting, exterior painting, commercial painting, drywall repair, pressure washing, deck staining.",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": [
          {
            "@type": "Service",
            "position": 1,
            "name": "Interior Painting",
            "description": "Professional interior painting services for homes and businesses using premium Sherwin Williams paints",
            "provider": {
              "@type": "LocalBusiness",
              "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
            }
          },
          {
            "@type": "Service",
            "position": 2,
            "name": "Exterior Painting",
            "description": "Expert exterior painting with weather-resistant paints and professional techniques",
            "provider": {
              "@type": "LocalBusiness",
              "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
            }
          },
          {
            "@type": "Service",
            "position": 3,
            "name": "Commercial Painting",
            "description": "Large-scale commercial painting projects for businesses and office buildings",
            "provider": {
              "@type": "LocalBusiness",
              "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
            }
          },
          {
            "@type": "Service",
            "position": 4,
            "name": "Drywall Repair",
            "description": "Complete drywall repair and carpentry services to prepare surfaces for painting",
            "provider": {
              "@type": "LocalBusiness",
              "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
            }
          },
          {
            "@type": "Service",
            "position": 5,
            "name": "Pressure Washing",
            "description": "Thorough pressure washing services to clean and prepare surfaces before painting",
            "provider": {
              "@type": "LocalBusiness",
              "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
            }
          },
          {
            "@type": "Service",
            "position": 6,
            "name": "Deck Staining",
            "description": "Professional deck staining and restoration services to protect outdoor spaces",
            "provider": {
              "@type": "LocalBusiness",
              "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
            }
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-brush-fill',
      title: t('interiorPainting'),
      description: 'Professional interior painting services for homes and businesses. We use premium Sherwin Williams paints to ensure beautiful, long-lasting results that transform your indoor spaces.',
      features: ['Color consultation', 'Surface preparation', 'Premium paint application', 'Clean-up included'],
      image: 'https://public.readdy.ai/ai/img_res/ddb6dc4078514fc791548218a6fe9cca.jpg'
    },
    {
      icon: 'ri-home-4-fill',
      title: t('exteriorPainting'),
      description: 'Protect and beautify your property with our expert exterior painting services. Weather-resistant paints and professional techniques ensure your investment is protected for years to come.',
      features: ['Weather-resistant paints', 'Power washing included', 'Trim and detail work', 'Multi-year warranty'],
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20house%20exterior%20with%20fresh%20professional%20paint%20job%2C%20modern%20residential%20home%2C%20attractive%20curb%20appeal%2C%20professional%20exterior%20painting%2C%20clean%20suburban%20setting&width=600&height=400&seq=exterior1&orientation=landscape'
    },
    {
      icon: 'ri-hammer-fill',
      title: t('drywall'),
      description: 'Complete drywall repair and carpentry services to ensure perfect surfaces for painting. From small patches to complete wall installations, we handle all your preparation needs.',
      features: ['Hole and crack repair', 'Texture matching', 'Custom carpentry', 'Surface smoothing'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20drywall%20repair%20work%20in%20progress%2C%20construction%20tools%2C%20home%20renovation%2C%20wall%20preparation%2C%20skilled%20craftsman%20working%20on%20interior%20walls&width=600&height=400&seq=drywall1&orientation=landscape'
    },
    {
      icon: 'ri-drop-fill',
      title: t('pressureWashing'),
      description: 'Thorough pressure washing services to clean and prepare surfaces before painting. Remove dirt, mildew, and old paint for optimal adhesion and finish quality.',
      features: ['High-pressure cleaning', 'Mildew removal', 'Surface preparation', 'Eco-friendly solutions'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20pressure%20washing%20service%20cleaning%20house%20exterior%2C%20high%20pressure%20water%20cleaning%20equipment%2C%20home%20maintenance%2C%20surface%20preparation%20work&width=600&height=400&seq=pressure1&orientation=landscape'
    },
    {
      icon: 'ri-road-map-fill',
      title: t('epoxyFloor'),
      description: 'Durable epoxy floor coatings for garages, basements, and commercial spaces. Create beautiful, easy-to-clean surfaces that resist stains, chemicals, and wear.',
      features: ['Chemical resistant', 'Easy to clean', 'Slip-resistant options', 'Custom colors available'],
      image: 'https://readdy.ai/api/search-image?query=Modern%20garage%20with%20glossy%20epoxy%20floor%20coating%2C%20professional%20floor%20finish%2C%20durable%20industrial%20flooring%2C%20clean%20organized%20space%2C%20reflective%20surface&width=600&height=400&seq=epoxy1&orientation=landscape'
    },
    {
      icon: 'ri-leaf-fill',
      title: t('deckStaining'),
      description: 'Professional deck staining and restoration services to protect and enhance your outdoor living spaces. Extend the life of your deck while improving its appearance.',
      features: ['Wood restoration', 'UV protection', 'Water resistance', 'Natural wood enhancement'],
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20wooden%20deck%20with%20fresh%20stain%20finish%2C%20outdoor%20living%20space%2C%20deck%20restoration%2C%20professional%20wood%20staining%2C%20backyard%20patio%20with%20furniture&width=600&height=400&seq=deck1&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen pt-8">
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            {t('servicesTitle')}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {t('servicesSubtitle')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="md:flex">
                  <div className="md:w-1/2">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-64 md:h-full object-cover object-top"
                    />
                  </div>
                  <div className="md:w-1/2 p-8">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                      <i className={`${service.icon} text-white text-xl`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <i className="ri-check-line text-green-500 mr-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact">
                      <Button size="sm">
                        {t('getQuote')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Our Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We follow a proven process to ensure exceptional results on every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Free estimate and project planning', icon: 'ri-chat-3-fill' },
              { step: '02', title: 'Preparation', description: 'Surface prep and protection', icon: 'ri-tools-fill' },
              { step: '03', title: 'Application', description: 'Professional paint application', icon: 'ri-brush-fill' },
              { step: '04', title: 'Completion', description: 'Final inspection and cleanup', icon: 'ri-check-double-fill' }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${process.icon} text-white text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-blue-600 mb-2">{process.step}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Contact us today for your free estimate and consultation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-mail-fill"></i>
                {t('getQuote')}
              </Button>
            </Link>
            <a href="tel:770-875-6990">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-phone-fill"></i>
                770-875-6990
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
